// Auth client. Browser → backend /auth/login (USER_PASSWORD_AUTH) →
// returns idToken in the body and sets the httpOnly refresh-token
// cookie. The Cognito JS SDK is intentionally NOT used — we don't
// want refresh tokens in JS memory (Security Ops §4.1) and we'd
// rather pay 1 round-trip than ship the SDK's 60+ kB.

import {
  startAuthentication as wnStartAuthentication,
  startRegistration as wnStartRegistration,
} from "@simplewebauthn/browser";

import { env } from "../env";
import {
  clearSession,
  completeDevSignIn,
  completeDevSignOut,
  getAccessToken,
  getIdToken,
  isDevAuthEnabled,
  setSession,
} from "./session";

// LoginResponse is a discriminated union — the api-auth Lambda returns
// one of four shapes depending on the user's MFA state. Callers must
// branch on `state`.
//
//   authenticated      — signed in, idToken in memory, refresh cookie set.
//   mfa_required       — user has TOTP enrolled, prompt for the 6-digit
//                        code and call respondToTotp(state, session, ...).
//   mfa_setup          — user must enrol now: render `secretCode` as a
//                        QR + ask for the first verification code, then
//                        call respondToTotp("mfa_setup", session, ...).
//   webauthn_required  — user has a passkey enrolled, fire navigator.
//                        credentials.get() with credentialRequestOptions
//                        and call respondToWebAuthn(session, ...).
export type LoginResponse =
  | {
      state: "authenticated";
      idToken: string;
      accessToken?: string;
      tokenType: string;
      expiresIn: number;
    }
  | { state: "mfa_required"; session: string }
  | { state: "mfa_setup"; session: string; secretCode: string }
  // Returned on an invited user's first sign-in: they logged in with their
  // temporary password and must now choose a permanent one. The SPA shows a
  // set-password screen and calls setNewPassword() with the session.
  | { state: "new_password_required"; session: string }
  | {
      state: "webauthn_required";
      session: string;
      credentialRequestOptions: PublicKeyCredentialRequestOptionsJSON;
    }
  // Returned when an admin (super_admin / tenant_admin) signs in
  // successfully but has no TOTP factor associated yet. The SPA must
  // route through /mfa-enrollment before any other navigation — no
  // ID token or refresh cookie is issued until enrolment completes.
  | { state: "mfa_enrollment_required"; accessToken: string }
  // Returned when the user has multiple MFA factors enrolled and no
  // preferred one set. The SPA shows a picker (passkey by default,
  // authenticator-app fallback) and POSTs the chosen factor to
  // /auth/mfa/select with the same session.
  | { state: "mfa_select"; session: string; availableFactors: string[] };

// PublicKeyCredentialRequestOptionsJSON / -CreationOptionsJSON are
// the W3C JSON dictionary types @simplewebauthn/browser already
// understands. We forward Cognito's payload verbatim — type-checked
// at the boundary, untyped through the middle.
export type PublicKeyCredentialRequestOptionsJSON = Parameters<
  typeof wnStartAuthentication
>[0]["optionsJSON"];
export type PublicKeyCredentialCreationOptionsJSON = Parameters<
  typeof wnStartRegistration
>[0]["optionsJSON"];

export interface PasskeyDescription {
  credentialId: string;
  friendlyName: string;
  relyingPartyId: string;
  authenticatorTransports: string[];
  authenticatorAttachment?: string;
  createdAt: string;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * Authenticate against /auth/login. On state=authenticated the API
 * sets an httpOnly refresh-token cookie scoped to /auth/refresh and
 * the SPA holds only the ID token in memory. On state=mfa_required
 * or state=mfa_setup the caller must render the appropriate UI and
 * call respondToTotp() with the session.
 *
 * The dev-bypass short-circuit is kept so local MSW sessions stay
 * one-click — it always returns state=authenticated.
 */
export async function signIn(email: string, password: string): Promise<LoginResponse> {
  if (isDevAuthEnabled()) {
    completeDevSignIn();
    return { state: "authenticated", idToken: "dev-bypass", tokenType: "Bearer", expiresIn: 3600 };
  }

  const res = await fetch(`${env.apiBaseUrl}/auth/login`, {
    method: "POST",
    credentials: "include", // accept the Set-Cookie response
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Sign-in failed");
  }

  const body = (await res.json()) as LoginResponse;
  if (body.state === "authenticated") {
    if (!body.idToken) throw new Error("Sign-in response missing ID token");
    setSession(body.idToken, body.accessToken ?? null);
  }
  return body;
}

/**
 * Respond to the SOFTWARE_TOKEN_MFA or MFA_SETUP challenge surfaced
 * by signIn(). On success returns state=authenticated and the SPA
 * has its session set; caller should navigate to the app.
 */
export async function respondToTotp(args: {
  state: "mfa_required" | "mfa_setup";
  session: string;
  email: string;
  code: string;
}): Promise<LoginResponse> {
  const res = await fetch(`${env.apiBaseUrl}/auth/mfa/totp/respond`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "MFA response failed");
  }
  const body = (await res.json()) as LoginResponse;
  if (body.state === "authenticated" && body.idToken) {
    setSession(body.idToken, body.accessToken ?? null);
  }
  return body;
}

/**
 * Complete an invited user's first sign-in: submit the permanent password
 * against the new_password_required challenge session from signIn(). On
 * success returns state=authenticated (session set) — or a follow-up MFA
 * challenge for admins, which the caller drives like any other login state.
 */
export async function setNewPassword(args: {
  email: string;
  session: string;
  newPassword: string;
}): Promise<LoginResponse> {
  const res = await fetch(`${env.apiBaseUrl}/auth/new-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Could not set your password");
  }
  const body = (await res.json()) as LoginResponse;
  if (body.state === "authenticated" && body.idToken) {
    setSession(body.idToken, body.accessToken ?? null);
  }
  return body;
}

/**
 * Respond to the WEB_AUTHN challenge surfaced by signIn(). The caller
 * has already invoked navigator.credentials.get() (via @simplewebauthn/
 * browser) and produced the AuthenticationResponseJSON. We forward it
 * to /auth/webauthn/authentication/verify; on success the SPA has its
 * session set identically to a state=authenticated /auth/login.
 */
export async function respondToWebAuthn(args: {
  session: string;
  email: string;
  credential: unknown;
}): Promise<LoginResponse> {
  const res = await fetch(`${env.apiBaseUrl}/auth/webauthn/authentication/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Passkey verification failed");
  }
  const body = (await res.json()) as LoginResponse;
  if (body.state === "authenticated" && body.idToken) {
    setSession(body.idToken, body.accessToken ?? null);
  }
  return body;
}

/**
 * Continuation of state=mfa_select. Posts the chosen factor to
 * /auth/mfa/select. Returns the next-state login response — typically
 * mfa_required for SOFTWARE_TOKEN_MFA or webauthn_required for
 * WEB_AUTHN — which the caller hands back to MfaChallengePage to
 * render the actual challenge UI.
 */
export async function selectMfaFactor(args: {
  session: string;
  email: string;
  factor: "WEB_AUTHN" | "SOFTWARE_TOKEN_MFA";
}): Promise<LoginResponse> {
  const res = await fetch(`${env.apiBaseUrl}/auth/mfa/select`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Could not select MFA factor");
  }
  return (await res.json()) as LoginResponse;
}

export async function refreshSession(): Promise<boolean> {
  if (isDevAuthEnabled()) {
    completeDevSignIn();
    return true;
  }

  const res = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    clearSession();
    return false;
  }

  const body = (await res.json().catch(() => ({}))) as {
    idToken?: unknown;
    accessToken?: unknown;
  };
  if (typeof body.idToken !== "string") {
    clearSession();
    return false;
  }

  setSession(body.idToken, typeof body.accessToken === "string" ? body.accessToken : null);
  return true;
}

// --- forced admin MFA enrolment --------------------------------------------
//
// Admins who sign in successfully but haven't enrolled MFA hit the
// state=mfa_enrollment_required branch above. The SPA holds their
// access token in memory (never persisted) and routes them through
// these two calls. No regular Authorization Bearer header is sent —
// the user has no ID token yet.

function enrollHeaders(accessToken: string): Record<string, string> {
  return { "Content-Type": "application/json", "X-Cognito-Access-Token": accessToken };
}

/** Begin forced MFA enrolment. Returns the TOTP secret to render as a QR code. */
export async function startMfaEnrollment(accessToken: string): Promise<{ secretCode: string }> {
  const res = await fetch(`${env.apiBaseUrl}/auth/mfa/enroll/start`, {
    method: "POST",
    credentials: "include",
    headers: enrollHeaders(accessToken),
    body: "{}",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Could not start MFA enrolment");
  }
  return (await res.json()) as { secretCode: string };
}

/**
 * Verify the first TOTP code + mark the factor as required for all
 * subsequent sign-ins. On success the SPA must direct the user back
 * to /sign-in — no tokens are issued here.
 */
export async function verifyMfaEnrollment(args: {
  accessToken: string;
  email: string;
  code: string;
}): Promise<void> {
  const res = await fetch(`${env.apiBaseUrl}/auth/mfa/enroll/verify`, {
    method: "POST",
    credentials: "include",
    headers: enrollHeaders(args.accessToken),
    body: JSON.stringify({ email: args.email, code: args.code }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "MFA verification failed");
  }
}

// --- passkey management (Settings → Security) ------------------------------
//
// The four management endpoints are authenticated by the API Gateway
// authorizer (it reads custom: claims from the ID token to populate
// tenant context — see backend/lambdas/api-auth audit writes). They
// also need the user's Cognito access token for the per-user passkey
// APIs Cognito requires; we send it via X-Cognito-Access-Token.

function authedHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const id = getIdToken();
  if (id) headers["Authorization"] = `Bearer ${id}`;
  const at = getAccessToken();
  if (at) headers["X-Cognito-Access-Token"] = at;
  return headers;
}

/**
 * Begin enrolment of a new passkey. Returns the
 * PublicKeyCredentialCreationOptionsJSON to hand to startRegistration().
 */
export async function startPasskeyRegistration(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  const res = await fetch(`${env.apiBaseUrl}/auth/webauthn/registration/options`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authedHeaders() },
    body: "{}",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Could not start passkey registration");
  }
  const body = (await res.json()) as {
    credentialCreationOptions: PublicKeyCredentialCreationOptionsJSON;
  };
  return body.credentialCreationOptions;
}

/**
 * Submit the AuthenticatorAttestation produced by startRegistration()
 * back to Cognito for verification + storage. Throws on failure.
 */
export async function completePasskeyRegistration(credential: unknown): Promise<void> {
  const res = await fetch(`${env.apiBaseUrl}/auth/webauthn/registration/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authedHeaders() },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Passkey registration failed");
  }
}

/** Convenience: full registration ceremony in one call. */
export async function registerPasskey(): Promise<void> {
  const optionsJSON = await startPasskeyRegistration();
  const credential = await wnStartRegistration({ optionsJSON });
  await completePasskeyRegistration(credential);
}

/** List the user's enrolled passkeys for the Settings panel. */
export async function listPasskeys(): Promise<PasskeyDescription[]> {
  const res = await fetch(`${env.apiBaseUrl}/auth/webauthn/credentials`, {
    method: "GET",
    credentials: "include",
    headers: authedHeaders(),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Could not list passkeys");
  }
  const body = (await res.json()) as { credentials: PasskeyDescription[] };
  return body.credentials;
}

/** Delete one passkey by credential ID. */
export async function deletePasskey(credentialId: string): Promise<void> {
  const res = await fetch(
    `${env.apiBaseUrl}/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: authedHeaders(),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error?.message ?? "Could not delete passkey");
  }
}

export async function signOut(): Promise<void> {
  // Match signIn: short-circuit on dev-bypass BEFORE the network call so
  // local MSW sessions don't fire a futile fetch every logout.
  if (isDevAuthEnabled()) {
    completeDevSignOut();
    return;
  }

  try {
    await fetch(`${env.apiBaseUrl}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Best-effort cookie clearing; in-memory session state cleared below.
  }

  clearSession();
}
